import HeaderBar from './HeaderBar.jsx'

/**
 * AppShell component provides the main layout structure for the /app route.
 * Combines header, session banner, global loader, and main content area.
 *
 * @param {Object} props - Component props
 * @param {number} props.flashcardCount - Total number of flashcards for header counter
 * @param {Function} props.onSignOut - Callback for sign out action
 * @param {React.ReactNode} props.children - Main content to render
 * @param {Object|null} [props.sessionBanner] - Session banner state (optional)
 * @param {boolean} [props.sessionBanner.visible] - Whether banner is visible
 * @param {string} [props.sessionBanner.variant] - Banner variant ('warning' or 'error')
 * @param {string} [props.sessionBanner.message] - Banner message
 * @param {Function} [props.onSessionBannerDismiss] - Callback to dismiss session banner
 * @param {boolean} [props.showGlobalLoader] - Whether to show global loader overlay
 * @returns {React.ReactElement} App shell layout
 */
export default function AppShell({
  flashcardCount,
  onSignOut,
  children,
  sessionBanner = null,
  onSessionBannerDismiss = null,
  showGlobalLoader = false,
}) {
  return (
    <div className="app-shell">
      {/* Header with counter and sign out */}
      <HeaderBar flashcardCount={flashcardCount} onSignOut={onSignOut} />

      {/* Session banner - shown above main content when needed */}
      {sessionBanner?.visible && (
        <div
          className={`session-banner session-banner--${sessionBanner.variant}`}
          role="alert"
        >
          <p className="session-banner__message">{sessionBanner.message}</p>
          {onSessionBannerDismiss && (
            <button
              type="button"
              className="session-banner__close"
              onClick={onSessionBannerDismiss}
              aria-label="Zamknij powiadomienie"
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Main content area */}
      <main className="app-shell__main">{children}</main>

      {/* Global loader overlay - shown during session initialization */}
      {showGlobalLoader && (
        <div className="global-loader" aria-live="polite" aria-busy="true">
          <div className="global-loader__spinner" aria-hidden="true"></div>
          <p className="global-loader__text">Ładowanie...</p>
        </div>
      )}
    </div>
  )
}
